import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { DataState } from "@/components/DataState"
import { ReasonDialog } from "@/components/ReasonDialog"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SelectorDeFecha } from "@/components/SelectorDeFecha"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { cn } from "@/lib/utils"

import { Advertencias } from "@/routes/holding/Advertencias"
import {
  declararParticipacion,
  fetchPerimetro,
  type SociedadDelPerimetro,
} from "@/routes/holding/api"
import { t } from "@/lib/i18n"

/**
 * La estructura del grupo: quien posee que y con que metodo entra al
 * consolidado.
 *
 * ES LA PANTALLA QUE QUITA EL SUPUESTO. Sin participaciones declaradas, el
 * consolidado asume el 100% por integracion global y lo dice en cada corrida.
 * Ese supuesto es razonable para un grupo de sociedades hermanas —el caso mas
 * comun de la region— y deja de serlo en cuanto una filial tiene socios
 * externos: el patrimonio y el resultado del grupo quedan sobrestimados.
 *
 * El metodo NO lo decide el porcentaje, lo decide el CONTROL. El porcentaje es
 * el indicio habitual y por eso se pide junto, pero la pantalla no lo elige
 * sola: un 45% con control de hecho consolida por integracion global, y un 60%
 * sin control no.
 */

const METODOS = [
  {
    id: "integracion_global",
    label: "Integración global",
    pista: "Hay control. Entra el 100% de cada línea y se reconoce el minoritario. NIIF 10.",
  },
  {
    id: "participacion",
    label: "Método de participación",
    pista: "Influencia significativa sin control. NO se suman sus líneas: entra una sola, la inversión. NIC 28.",
  },
  {
    id: "costo",
    label: "Costo",
    pista: "Sin influencia. La inversión se queda a su costo y solo entra el dividendo cobrado.",
  },
]

/**
 * YA NO ES UNA PESTAÑA: vive al pie de Consolidado. Son cuatro filas que se
 * declaran una vez y rara vez cambian; una pestaña entera para eso hacía
 * parecer que había algo que mirar cada mes. Está donde se nota su efecto —el
 * consolidado dice «asumí el 100%» justo encima—. `embebida` apaga las
 * advertencias propias, porque el consolidado ya trae las suyas.
 */
export function EstructuraPage({ embebida = false }: { embebida?: boolean } = {}) {
  const qc = useQueryClient()
  const { toast } = useToast()
  const [editando, setEditando] = React.useState<SociedadDelPerimetro | null>(null)
  const [porcentaje, setPorcentaje] = React.useState("100")
  const [metodo, setMetodo] = React.useState("integracion_global")
  const [desde, setDesde] = React.useState(() => new Date().toISOString().slice(0, 10))

  const q = useQuery({ queryKey: ["perimetro"], queryFn: () => fetchPerimetro() })

  const guardar = useMutation({
    mutationFn: (motivo: string) =>
      declararParticipacion({
        entity_id: editando!.entity_id,
        matriz_entity_id: editando!.matriz_entity_id,
        porcentaje: Number(porcentaje),
        metodo,
        vigente_desde: desde,
        motivo,
      }),
    onSuccess: () => {
      toast({ title: "Declarado. Marty lo anotó." })
      setEditando(null)
      void qc.invalidateQueries({ queryKey: ["perimetro"] })
      void qc.invalidateQueries({ queryKey: ["consolidado"] })
    },
    onError: (e: unknown) =>
      toast({
        variant: "destructive",
        title: "No se pudo declarar",
        description: e instanceof Error ? e.message : String(e),
      }),
  })

  function abrir(s: SociedadDelPerimetro) {
    setEditando(s)
    setPorcentaje(String(s.porcentaje))
    setMetodo(s.metodo)
  }

  const d = q.data
  return (
    <div className="space-y-3">
      {d && !embebida ? (
        <Advertencias
          advertencias={
            d.supuesto && d.nota
              ? [{ clave: "perimetro_supuesto", grave: true, texto: d.nota }]
              : []
          }
        />
      ) : null}

      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && (d?.sociedades.length ?? 0) === 0}
        emptyMessage={t("No hay sociedades en este cliente.")}
        onRetry={() => void q.refetch()}
        rows={4}
        columns={[26, 10, 12, 14, 22, 16]}
      >
        <WorkPanel
          title={t("Estructura del grupo")}
          meta={`${d?.sociedades.length ?? 0} sociedades · ${t("quién es dueño de quién, y de eso depende cómo se consolida")}`}
          footnote={t("Cambiar una participación no edita la anterior: la cierra y abre una nueva. Un período ya consolidado tiene que seguir dando el mismo número cuando se recalcule.")}
        >
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[18rem]">{t("Sociedad")}</TableHead>
                <TableHead className="w-14">{t("País")}</TableHead>
                <TableHead className="w-20">{t("Moneda")}</TableHead>
                <TableHead className="w-24 text-right">{t("% del grupo")}</TableHead>
                <TableHead className="w-[14rem]">{t("Método")}</TableHead>
                <TableHead className="w-28">{t("Origen")}</TableHead>
                <TableHead className="w-28 text-right">{t("Acciones")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d?.sociedades.map((s) => (
                <TableRow key={s.entity_id}>
                  <TableCell className="truncate font-medium" title={s.razon_social}>
                    {s.razon_social}
                    {s.es_cabecera ? (
                      <Badge variant="brand-soft" className="ml-2">
                        cabecera
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="code">{s.pais}</TableCell>
                  <TableCell className="code">{s.moneda_funcional}</TableCell>
                  <TableCell className={cn("num", !s.declarado && "text-muted-foreground")}>
                    {Number(s.porcentaje).toFixed(2)}
                  </TableCell>
                  <TableCell className="truncate text-xs">
                    {METODOS.find((m) => m.id === s.metodo)?.label ?? s.metodo}
                  </TableCell>
                  <TableCell>
                    {s.declarado ? (
                      <Badge variant="success-soft" estado>
                        declarado
                      </Badge>
                    ) : (
                      <Badge variant="warning-soft" estado title={t("Marty lo asumió; nadie lo declaró")}>
                        supuesto
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="xs" variant="outline" onClick={() => abrir(s)}>
                      {t("Declarar")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorkPanel>
      </DataState>

      {editando ? (
        <ReasonDialog
          open
          onOpenChange={(v) => {
            if (!v) setEditando(null)
          }}
          title={`Participación en ${editando.razon_social}`}
          description={t("El método no lo decide el porcentaje: lo decide el control. Un 45% con control de hecho consolida por integración global, y un 60% sin control no.")}
          summary={
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="pct">{t("% del capital que tiene el grupo")}</Label>
                  <Input
                    id="pct"
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={porcentaje}
                    onChange={(e) => setPorcentaje(e.target.value)}
                    className="num"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="desde">{t("Vigente desde")}</Label>
                  <SelectorDeFecha id="desde" value={desde} onChange={setDesde} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("Método de consolidación")}</Label>
                {METODOS.map((m) => (
                  <label
                    key={m.id}
                    className={cn(
                      "flex cursor-pointer items-start gap-2 rounded-md border p-2",
                      metodo === m.id ? "border-brand bg-brand-soft" : "hover:bg-accent/50",
                    )}
                  >
                    <input
                      type="radio"
                      name="metodo"
                      value={m.id}
                      checked={metodo === m.id}
                      onChange={() => setMetodo(m.id)}
                      className="mt-0.5"
                    />
                    <span className="min-w-0">
                      <span className="block text-xs font-medium">{m.label}</span>
                      <span className="mt-px block text-2xs leading-[0.95rem] text-muted-foreground">
                        {m.pista}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          }
          confirmLabel={t("Declarar")}
          pending={guardar.isPending}
          onConfirm={(motivo) => guardar.mutate(motivo)}
        />
      ) : null}
    </div>
  )
}
